import React, { useState } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { Box } from '@material-ui/core';
import { injectIntl, intlShape } from '../../util/reactIntl';
import {
  IconArrowHead,
  IconCard,
  IconCheckmark,
  IconClose,
  InlineTextButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  TypographyWrapper,
} from '..';
import DeletePaymentMethodModal from './DeletePaymentMethodModal';
import { TypographyFormat } from '../TypographyWrapper/TypographyWrapper';
import css from './SavedCardDetails.module.css';

const DEFAULT_CARD = 'defaultCard';
export const REPLACE_CARD = 'replaceCard';

const SavedCardDetails = (props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState(DEFAULT_CARD);

  const {
    rootClassName,
    className,
    intl,
    card,
    onChange,
    onDeleteCard,
    onManageDisableScrolling,
    deletePaymentMethodInProgress,
  } = props;

  const { last4Digits, expirationMonth, expirationYear, brand } = card || {};
  const classes = classNames(rootClassName || css.root, className);

  const paymentMethodPlaceholderDesktop = intl.formatMessage(
    { id: 'SavedCardDetails.savedPaymentMethodPlaceholderDesktop' },
    { last4Digits }
  );

  const paymentMethodPlaceholderMobile = intl.formatMessage(
    { id: 'SavedCardDetails.savedPaymentMethodPlaceholderMobile' },
    { last4Digits }
  );

  const paymentMethodPlaceholder = (
    <>
      <span className={css.paymentMethodPlaceholderDesktop}>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
          {paymentMethodPlaceholderDesktop}
        </TypographyWrapper>
      </span>
      <span className={css.paymentMethodPlaceholderMobile}>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
          {paymentMethodPlaceholderMobile}
        </TypographyWrapper>
      </span>
    </>
  );

  const replaceCardText = intl.formatMessage({
    id: 'SavedCardDetails.replaceCardText',
  });
  const replaceCard = (
    <span>
      <IconCard brand="default" className={css.cardIcon} />
      <TypographyWrapper
        format={TypographyFormat.Default}
        variant="body1"
        typographyOverrides={{ display: 'inline' }}
      >
        {replaceCardText}
      </TypographyWrapper>
    </span>
  );

  const expiredCardText = intl.formatMessage(
    { id: 'SavedCardDetails.expiredCardText' },
    { last4Digits }
  );
  const expiredText = <div className={css.cardExpiredText}>{expiredCardText}</div>;

  const isExpired = (expMonth, expYear) => {
    const currentTime = new Date();
    const currentYear = currentTime.getFullYear();
    const currentMonth = currentTime.getMonth() + 1; // getMonth() method returns the month (from 0 to 11)

    if (expYear < currentYear) {
      return true;
    }
    if (expYear === currentYear && expMonth < currentMonth) {
      return true;
    }

    return false;
  };

  const isCardExpired =
    expirationMonth && expirationYear && isExpired(expirationMonth, expirationYear);

  const defaultCard = (
    <div className={css.savedPaymentMethod}>
      <IconCard brand={brand} className={css.cardIcon} />

      <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }} isDynamicValue>
        {paymentMethodPlaceholder}
      </TypographyWrapper>
      <span className={isCardExpired ? css.expirationDateExpired : css.expirationDate}>
        <TypographyWrapper
          variant="body1"
          typographyOverrides={{ display: 'inline' }}
          isDynamicValue
        >
          {expirationMonth}/{expirationYear.toString().substring(2)}
        </TypographyWrapper>
      </span>
    </div>
  );

  const handleClick = (item) => (e) => {
    // Clicking buttons inside a form will call submit
    e.preventDefault();
    e.stopPropagation();

    setActive(item);
    setMenuOpen(false);
    if (onChange) {
      onChange(item);
    }
  };

  const onToggleActive = (isOpen) => {
    setMenuOpen(isOpen);
  };

  const handleDeleteCard = () => {
    setIsModalOpen(true);
  };

  const iconArrowClassName = menuOpen ? css.IconArrowAnimation : null;

  const replaceCardTitle = intl.formatMessage({
    id: 'SavedCardDetails.replaceCardTitle',
  });
  const deletePaymentMethod = intl.formatMessage({ id: 'SavedCardDetails.deletePaymentMethod' });

  const showExpired = isCardExpired && active === DEFAULT_CARD;
  const menuLabelId = 'saved-card-details-label';
  const menuContentId = 'saved-card-details-content';

  return (
    <div className={classes}>
      <Menu className={css.menu} isOpen={menuOpen} onToggleActive={onToggleActive} useArrow={false}>
        <MenuLabel
          id={menuLabelId}
          className={css.menuLabel}
          ariaLabel="Saved Card Details Menu"
          contentId={menuContentId}
        >
          <div className={showExpired ? css.menuLabelWrapperExpired : css.menuLabelWrapper}>
            {active === DEFAULT_CARD ? defaultCard : replaceCard}
            <span>
              <IconArrowHead
                direction="down"
                size="small"
                rootClassName={css.iconArrow}
                className={iconArrowClassName}
              />
            </span>
          </div>
        </MenuLabel>
        <MenuContent id={menuContentId} labelId={menuLabelId} rootClassName={css.menuContent}>
          <MenuItem key="first item" className={css.menuItem}>
            <IconCheckmark
              className={active === DEFAULT_CARD ? css.iconCheckmark : css.iconCheckmarkHidden}
              size="small"
            />
            <InlineTextButton className={css.menuText} onClick={handleClick(DEFAULT_CARD)}>
              {defaultCard}
            </InlineTextButton>
          </MenuItem>
          <MenuItem key="divider" className={css.menuDivider} role="separator">
            <TypographyWrapper variant="body1">{replaceCardTitle}</TypographyWrapper>
          </MenuItem>
          <MenuItem key="second item" className={css.menuItem}>
            <IconCheckmark
              className={active === REPLACE_CARD ? css.iconCheckmark : css.iconCheckmarkHidden}
              size="small"
            />
            <InlineTextButton
              className={css.menuTextReplaceCard}
              onClick={handleClick(REPLACE_CARD)}
            >
              {replaceCard}
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
      {showExpired && !menuOpen ? expiredText : null}

      {onDeleteCard && active !== REPLACE_CARD ? (
        <InlineTextButton onClick={handleDeleteCard} className={css.savedPaymentMethodDelete}>
          <Box display="flex" alignItems="center">
            <IconClose size="small" className={css.deleteCardCloseIcon} />
            <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
              {deletePaymentMethod}
            </TypographyWrapper>
          </Box>
        </InlineTextButton>
      ) : null}

      <DeletePaymentMethodModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDeleteCard={onDeleteCard}
        deletePaymentMethodInProgress={deletePaymentMethodInProgress}
        last4Digits={last4Digits}
      />
    </div>
  );
};

SavedCardDetails.defaultProps = {
  rootClassName: null,
  className: null,
  card: null,
  onChange: null,
  onDeleteCard: null,
  deletePaymentMethodInProgress: false,
  onManageDisableScrolling: null,
};

SavedCardDetails.propTypes = {
  rootClassName: string,
  className: string,
  intl: intlShape.isRequired,
  card: shape({
    brand: string.isRequired,
    expirationMonth: number.isRequired,
    expirationYear: number.isRequired,
    last4Digits: string.isRequired,
  }),
  onChange: func,
  onDeleteCard: func,
  onManageDisableScrolling: func,
  deletePaymentMethodInProgress: bool,
};

export default injectIntl(SavedCardDetails);
